import React, {useState, useEffect} from 'react'
import 'bootstrap/dist/css/bootstrap.css';
import { coreEx, legHold, quadStretch, calf, resume } from './stretches'
import { clearLocalStorage } from './resumablestretch'
import { execute, stopQueue, setSession } from './workers/queue'
import Streakcounter from './components/streakcounter'
import { FILES } from './audio/files'
import { CONCAT_AUDIO } from './audio/concatFiles'
import {dbOnLoad, addStretch} from './db/StretchRecord'

const synth = window.speechSynthesis;
var audioContext;
var currentConcatAudio;

dbOnLoad();

async function say(text) {
  if (FILES[text]) {
    await play(text)
  } else {
    await ttsSay(text);
  }
}

async function play(text) {
  const audioObj = new Audio(FILES[text])
  const track = audioContext.createMediaElementSource(audioObj);
  track.connect(audioContext.destination);
  await new Promise(function(resolve) {
    audioObj.onended = (e) => {
      resolve();
    }
    audioObj.play();
  });
}

async function ttsSay(text) {
  synth.cancel()
  const ssu = new SpeechSynthesisUtterance(text);
  await new Promise(function(resolve) {
    ssu.onend = (e) => {
      synth.cancel()
      resolve();
    }
    synth.speak(ssu);
  });
}

async function consumer() {
    const executeIterator = execute();
  for await (const text of executeIterator) {
    await say(text)
  }
}

function clickEx(exFn, stretchState) {
  return () => {
    if (!audioContext) {
      audioContext = new AudioContext();
    }

    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    if (audioContext.state === 'closed') {
      audioContext = new AudioContext();
    }

    exFn(stretchState);
    consumer();
    navigator.mediaSession.setActionHandler('stop', function() {
      stop();
    });
  }
}

function applyEvent(event, setActive, setRep) {
  switch (event.command) {
    case 'setActive':
      setActive(event.args[0]);
      break;
    case 'setRep':
      setRep(event.args[0]);
      break;
    case 'metadata':
      setSession(event.args[0], event.args[1]);
      break;
    default:
      console.log('unrecognized scheduled event');
  }
}

// Plays a stretch's pre-built concatenated mp3 instead of driving execute()'s
// real-time say/sleep loop. setActive/setRep/metadata fire on a schedule of
// offsets (computed at build time from the same command queue) so they land
// at the same point in the stretch as they would have originally. Recording
// completion is tied to the audio's `ended` event rather than to a queue
// position, so it still fires even if the tab is backgrounded and any
// setTimeouts are throttled.
function clickConcat(stretchKey, stretchState) {
  return () => {
    if (!audioContext) {
      audioContext = new AudioContext();
    }

    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    if (audioContext.state === 'closed') {
      audioContext = new AudioContext();
    }

    const { setActive, setRep } = stretchState;
    const { audio, recordName, events } = CONCAT_AUDIO[stretchKey];
    const pending = [...events].sort((a, b) => a.offsetMs - b.offsetMs);

    const audioObj = new Audio(audio);
    const track = audioContext.createMediaElementSource(audioObj);
    track.connect(audioContext.destination);

    audioObj.ontimeupdate = () => {
      const currentMs = audioObj.currentTime * 1000;
      while (pending.length > 0 && pending[0].offsetMs <= currentMs) {
        applyEvent(pending.shift(), setActive, setRep);
      }
    };

    audioObj.onended = () => {
      addStretch(recordName);
      setActive("");
      setRep("");
      clearLocalStorage();
      currentConcatAudio = undefined;
    };

    currentConcatAudio = audioObj;
    audioObj.play();

    navigator.mediaSession.setActionHandler('stop', function() {
      stop(setActive, setRep);
    });
  }
}

function clickResume(lsActive, lsRep, stretchState) {
  if (!audioContext) {
    audioContext = new AudioContext();
  }

  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }

  if (audioContext.state === 'closed') {
    audioContext = new AudioContext();
  }

  resume(lsActive, lsRep, stretchState)
  consumer();
  navigator.mediaSession.setActionHandler('stop', function() {
    stop();
  });
}

async function stop(setActive, setRep) {
  synth.pause();
  if (currentConcatAudio) {
    currentConcatAudio.pause();
    currentConcatAudio = undefined;
  }
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.suspend();
    await audioContext.close();
  }
  stopQueue();
  synth.cancel();
  clearLocalStorage();
  setActive("");
  setRep("");
}

function App() {
  const [active, setActive] = useState("");
  const [rep, setRep] = useState("");
  const [concat, setConcat] = useState(false);
  const stretchState = {setActive, setRep};
  const lsActive = localStorage.getItem('active')
  const lsRep = localStorage.getItem('rep')
  const resumePossible = active === "" && lsActive !== null && lsActive !== "";
  useEffect(() => {
    if (lsActive !== active && active !== "") {
      console.log(`active: ${active}`)
      localStorage.setItem('active', active)
    }
    if (lsRep !== rep && rep !== "") {
      console.log(`rep: ${rep}`)
      localStorage.setItem('rep', rep)
    }
    if (active === "" && rep === "") {
      console.log("clearing")
      localStorage.setItem('active', active)
      localStorage.setItem('rep', rep)
    }
  })
  return (
    <div className="container">
      <div className="row pt-5 justify-content-between">
        <button className="btn btn-primary col-3 mx-auto" onClick={concat ? clickConcat('coreEx', stretchState) : clickEx(coreEx, stretchState)}>core</button>
        <button className="btn btn-primary col-3 mx-auto" onClick={concat ? clickConcat('legHold', stretchState) : clickEx(legHold, stretchState)}>leg hold</button>
      </div>
      <div className="row pt-5 justify-content-between">
        <button className="btn btn-primary col-3 mx-auto" onClick={concat ? clickConcat('quadStretch', stretchState) : clickEx(quadStretch, stretchState)}>quad</button>
        <button className="btn btn-primary col-3 mx-auto" onClick={concat ? clickConcat('calf', stretchState) : clickEx(calf, stretchState)}>calf</button>
      </div>
      <div className="row pt-5 justify-content-center">
        <button className="btn btn-danger col-2 mx-auto" onClick={() =>{ stop(setActive, setRep)}}>stop</button>
      </div>
      <div className="row justify-content-center mt-3">
        <p className="col text-center">current: {active}</p>
      </div>
      <div className="row justify-content-center mb-0">
        <p className="col text-center mb-0">rep: {rep}</p>
      </div>
      <Streakcounter active={active} type={"main"}/>
      <div className="row pt-5 justify-content-between">
        <button className={`btn btn-${concat ? 'dark' : 'light'} col-3 mx-auto`} onClick={() => {setConcat(!concat)}}>concat</button>
      </div>
      <div className="row pt-5 justify-content-between">
        <button className={`btn btn-${resumePossible ? 'dark' : 'light'} col-3 mx-auto`} onClick={() => {clickResume(lsActive, lsRep, stretchState)}}>resume</button>
      </div>
      <div className="fixed-bottom">
        <p className="text-monospace" style={{"fontSize":"0.5rem"}}>v4.0.0</p>
      </div>
    </div>
  );
}

export default App;
